import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { type GridRenderCellParams } from "@mui/x-data-grid";
import { memo, useEffect, useRef, useState } from "react";

interface GridCellExpandProps {
  value: string;
  width: number;
}

function isOverflown(element: Element): boolean {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const GridCellExpand = memo(function GridCellExpand(
  props: GridCellExpandProps,
) {
  const { width, value } = props;
  let tooltipValue = value;
  if (Array.isArray(value)) {
    tooltipValue = value.join("\n");
  }
  const wrapper = useRef<HTMLDivElement | null>(null);
  const cellDiv = useRef<HTMLDivElement | null>(null);
  const cellValue = useRef<HTMLDivElement | null>(null);
  const [_anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFullCellVisible, setIsFullCellVisible] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const handleMouseEnter = () => {
    const valueEl = cellValue.current!;
    const isCurrentlyOverflown =
      (Array.isArray(value) && value.length > 0) || isOverflown(valueEl);
    setIsTooltipVisible(isCurrentlyOverflown);
    setAnchorEl(wrapper.current);
    setIsFullCellVisible(true);
  };

  const handleMouseLeave = () => {
    setIsFullCellVisible(false);
  };

  useEffect(() => {
    if (!isFullCellVisible) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      if (nativeEvent.key === "Escape") {
        setIsFullCellVisible(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setIsFullCellVisible, isFullCellVisible, isTooltipVisible]);

  return (
    <Stack
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      alignItems="center"
      sx={{
        lineHeight: "24px",
        width: "100%",
        height: "100%",
        position: "relative",
        padding: "0 10px",
      }}
      justifyContent="center"
    >
      <Box
        ref={cellDiv}
        sx={{
          height: "100%",
          width,
          display: "block",
          position: "absolute",
          top: 0,
        }}
      />
      <Box
        ref={cellValue}
        sx={{
          width: `calc(${width}px - 20px)`,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          boxSizing: "border-box",
        }}
      >
        {Array.isArray(value) && value.length > 0 ? value[0] + "..." : value}
      </Box>
      <Tooltip
        title={tooltipValue}
        open={isFullCellVisible && isTooltipVisible}
        placement="bottom"
        disableInteractive
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: { offset: [0, 4] },
              },
            ],
          },
        }}
      >
        <Box />
      </Tooltip>
    </Stack>
  );
});

export function ExpandCell(params: GridRenderCellParams) {
  return (
    <GridCellExpand
      value={params.value || ""}
      width={params.colDef.computedWidth}
    />
  );
}
